<template>
  <div class="user-requests-list text-dark" :class="{'user-requests-list--user': isUserView}">
    <h1 v-if="!isUserView">{{ i18n['absence_requests'] }}</h1>

    <div class="user-requests-list__toolbar marg-1-bottom">
      <Tabs
        :selected="selectedTab"
        :tabs="tabs"
        @select="selectedTab = $event">
      </Tabs>

      <DoFiltersFilter
        :title="i18n['school_year']"
        :tags="schoolYearTags"
        :selectedTags="selectedSchoolYears"
        @select="toggleSchoolYear"
        @clear="selectedSchoolYears = []">
      </DoFiltersFilter>
    </div>

    <Loader v-if="isLoading"></Loader>

    <table class="do-table" v-else>
      <thead class="do-table__header">
      <tr>
        <th
          class="th-sorter td-created"
          role="button"
          tabindex="0"
          :class="{'th-sorter--active': sortBy === 'created_at'}"
          @click="setSort('created_at')"
          @keydown.enter.prevent="setSort('created_at')">
          {{ i18n['created'] }} <span class="icon-menu-open"></span>
        </th>
        <th
          class="th-sorter"
          role="button"
          tabindex="0"
          :class="{'th-sorter--active': sortBy === 'vUserName'}"
          @click="setSort('vUserName')"
          @keydown.enter.prevent="setSort('vUserName')">
          {{ i18n['who'] }} <span class="icon-menu-open"></span>
        </th>
        <th
          class="th-sorter td-date"
          role="button"
          tabindex="0"
          :class="{'th-sorter--active': sortBy === 'start_time'}"
          @click="setSort('start_time')"
          @keydown.enter.prevent="setSort('start_time')">
          {{ i18n['from'] }} <span class="icon-menu-open"></span>
        </th>
        <th
          class="th-sorter td-date"
          role="button"
          tabindex="0"
          :class="{'th-sorter--active': sortBy === 'end_time'}"
          @click="setSort('end_time')"
          @keydown.enter.prevent="setSort('end_time')">
          {{ i18n['to'] }} <span class="icon-menu-open"></span>
        </th>
        <th
          class="th-sorter"
          role="button"
          tabindex="0"
          :class="{'th-sorter--active': sortBy === 'vSubjectTitle'}"
          @click="setSort('vSubjectTitle')"
          @keydown.enter.prevent="setSort('vSubjectTitle')">
          {{ i18n['reason'] }} <span class="icon-menu-open"></span>
        </th>
        <th
          class="th-sorter"
          role="button"
          tabindex="0"
          :class="{'th-sorter--active': sortBy === 'request_note'}"
          @click="setSort('request_note')"
          @keydown.enter.prevent="setSort('request_note')">
          {{ i18n['note'] }} <span class="icon-menu-open"></span>
        </th>
        <th
          v-if="selectedTab !== 'requested'"
          class="th-sorter"
          role="button"
          tabindex="0"
          :class="{'th-sorter--active': sortBy === 'response_note'}"
          @click="setSort('response_note')"
          @keydown.enter.prevent="setSort('response_note')">
          {{ i18n['note_back'] }} <span class="icon-menu-open"></span>
        </th>
        <th
          v-if="selectedTab !== 'requested'"
          class="th-sorter"
          role="button"
          tabindex="0"
          :class="{'th-sorter--active': sortBy === 'vResponderName'}"
          @click="setSort('vResponderName')"
          @keydown.enter.prevent="setSort('vResponderName')">
          {{ i18n['answered_by'] }} <span class="icon-menu-open"></span>
        </th>
        <th
          v-if="selectedTab === 'requested'">
        </th>
        <th
          v-else
          class="th-sorter"
          role="button"
          tabindex="0"
          :class="{'th-sorter--active': sortBy === 'state'}"
          @click="setSort('state')"
          @keydown.enter.prevent="setSort('state')">
          {{ i18n['status'] }} <span class="icon-menu-open"></span>
        </th>
      </tr>
      </thead>

      <tbody>
      <tr v-if="sortedRequests.length === 0">
        <td colspan="7" class="italic text-align-left">
          {{ emptyTableTitle }}
        </td>
      </tr>

      <tr v-for="request in sortedRequests"
          :key="request.id"
          @click="setTheEditRequest(request)">
        <td class="td-created"
            :content="request.vCreated"
            v-tippy="{
                arrow: true,
                animateFill: false,
                animation : 'shift-away',
                zIndex: '999999992',
                boundary: 'window'
              }">
          {{ request.vCreatedTimeAgo }}
        </td>

        <td>{{ request.vUserName }}</td>
        <td class="td-date">{{ request.vStart }}</td>
        <td class="td-date">{{ request.vEnd }}</td>

        <td>
          <span class="do-pill do-pill--white">{{ request.vSubjectTitle }}</span>
        </td>

        <td class="italic">{{ request.request_note }}</td>
        <td class="italic" v-if="selectedTab !== 'requested'">{{ request.response_note }}</td>
        <td v-if="selectedTab !== 'requested'">{{ request.vResponderName }}</td>

        <td>
          <button class="do-bttn"
                  v-if="!isUserView && selectedTab === 'requested'"
                  @click="theAnswerRequest = request">
            {{ i18n['answer'] }}
          </button>

          <div class="d-bagde d-badge--yellow" v-if="isUserView && request.state === 'requested'">
            {{ i18n['pending'] }}
          </div>
          <div class="d-bagde d-badge--green" v-else-if="request.state === 'approved'">
            {{ i18n['approved'] }}
          </div>
          <div class="d-bagde" v-if="request.state === 'rejected'">
            {{ i18n['rejected'] }}
          </div>
        </td>
      </tr>
      </tbody>
    </table>

    <UserRequestListAnswer
      v-if="theAnswerRequest"
      :request="theAnswerRequest"
      @submitted="onSubmittedAnswer"
      @close="onCloseAnswerRequest">
    </UserRequestListAnswer>

    <UserRequestForm
      v-if="theEditRequest"
      :editRequest="theEditRequest"
      @submitted="onSubmittedAnswer"
      @close="theEditRequest = null">
    </UserRequestForm>
  </div>
</template>

<script>
import moment from 'moment'
import docendo from '../../docendo'
import UserRequestListAnswer from './UserRequestListAnswer.vue'
import Tabs from '../common/Tabs.vue'
import Loader from '../common/Loader.vue'
import UserRequest from './UserRequest.vue'
import UserRequestForm from './UserRequestForm.vue'
import DoFiltersFilter from '../DoFilters/DoFiltersFilter.vue'
import { mapGetters } from 'vuex'

const SORT_STORAGE_KEY = 'docendo-user-requests-list-sort'
const SORTABLE_KEYS = [
  'created_at',
  'vUserName',
  'start_time',
  'end_time',
  'vSubjectTitle',
  'request_note',
  'response_note',
  'vResponderName',
  'state'
]
const DATE_SORT_KEYS = {
  created_at: true,
  start_time: true,
  end_time: true
}

const I18N = {
  da: {
    absence_requests: 'Fraværsanmodninger',
    created: 'Oprettet',
    who: 'Hvem',
    from: 'Fra',
    to: 'Til',
    reason: 'Årsag',
    note: 'Note',
    note_back: 'Note tilbage',
    answered_by: 'Besvaret af',
    status: 'Status',
    tab_requested: 'Afventer svar',
    tab_approved: 'Godkendte',
    tab_rejected: 'Afviste',
    empty_requested: 'Ingen nye anmodninger',
    empty_approved: 'Ingen godkendte anmodninger',
    empty_rejected: 'Ingen afviste anmodninger',
    answer: 'Besvar',
    pending: 'Afventer svar',
    approved: 'Godkendt',
    rejected: 'Afvist',
    at_time_cap: 'Kl.',
    at_time_lower: 'kl.',
    ago_suffix: 'siden',
    school_year: 'Skoleår'
  },
  en: {
    absence_requests: 'Absence requests',
    created: 'Created',
    who: 'Who',
    from: 'From',
    to: 'To',
    reason: 'Reason',
    note: 'Note',
    note_back: 'Reply note',
    answered_by: 'Answered by',
    status: 'Status',
    tab_requested: 'Pending',
    tab_approved: 'Approved',
    tab_rejected: 'Rejected',
    empty_requested: 'No new requests',
    empty_approved: 'No approved requests',
    empty_rejected: 'No rejected requests',
    answer: 'Answer',
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected',
    at_time_cap: 'At',
    at_time_lower: 'at',
    ago_suffix: 'ago',
    school_year: 'School year'
  },
  nb: {
    absence_requests: 'Fraværsforespørsler',
    created: 'Opprettet',
    who: 'Hvem',
    from: 'Fra',
    to: 'Til',
    reason: 'Årsak',
    note: 'Merknad',
    note_back: 'Svarmerknad',
    answered_by: 'Besvart av',
    status: 'Status',
    tab_requested: 'Venter svar',
    tab_approved: 'Godkjente',
    tab_rejected: 'Avviste',
    empty_requested: 'Ingen nye forespørsler',
    empty_approved: 'Ingen godkjente forespørsler',
    empty_rejected: 'Ingen avviste forespørsler',
    answer: 'Svar',
    pending: 'Venter svar',
    approved: 'Godkjent',
    rejected: 'Avvist',
    at_time_cap: 'Kl.',
    at_time_lower: 'kl.',
    ago_suffix: 'siden',
    school_year: 'Skoleår'
  }
}

export default {
  components: { UserRequestForm, UserRequest, Loader, Tabs, UserRequestListAnswer, DoFiltersFilter },
  props: {
    isUserView: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isLoading: false,
      requests: [],
      isLoadingSubjects: false,
      subjects: [],
      theEditRequest: null,
      theAnswerRequest: null,
      selectedTab: 'requested',
      sortBy: 'created_at',
      sortDir: 'desc',
      selectedSchoolYears: []
    }
  },
  created () {
    this.restoreSort()
  },
  computed: {
    ...mapGetters([
      'schoolId',
      'allUsersInHash',
      'locale'
    ]),
    activeSchoolYears () {
      return (this.$store.state.activeSchoolYears || []).slice().sort((a, b) => {
        return moment(b.student_time_start || b.employee_time_start).valueOf() -
          moment(a.student_time_start || a.employee_time_start).valueOf()
      })
    },
    schoolYearTags () {
      return this.activeSchoolYears.map(year => year.name)
    },
    i18n () {
      return I18N[this.locale] || I18N['da']
    },
    emptyTableTitle () {
      if (this.selectedTab === 'requested') {
        return this.i18n['empty_requested']
      }
      if (this.selectedTab === 'approved') {
        return this.i18n['empty_approved']
      }
      if (this.selectedTab === 'rejected') {
        return this.i18n['empty_rejected']
      }
    },
    tabs () {
      return [
        { id: 'requested', title: this.i18n['tab_requested'] },
        { id: 'approved', title: this.i18n['tab_approved'] },
        { id: 'rejected', title: this.i18n['tab_rejected'] }
      ]
    },
    filteredRequests () {
      const tabRequests = this.requests.filter(r => r.state === this.selectedTab)
      if (this.selectedSchoolYears.length === 0) {
        return tabRequests
      }
      const selectedYears = this.activeSchoolYears.filter(year => this.selectedSchoolYears.indexOf(year.name) > -1)
      return tabRequests.filter(request => {
        return selectedYears.some(year => this.requestInSchoolYear(request, year))
      })
    },
    formattedRequests () {
      return this.filteredRequests.map(r => {
        const created = moment(r.created_at)
        const start = moment(r.start_time)
        const end = moment(r.end_time)

        return {
          ...r,
          vCreated: created.format('ll [' + this.i18n['at_time_cap'] + '] LT'),
          vCreatedTimeAgo: moment.duration(moment().diff(created)).humanize() + ' ' + this.i18n['ago_suffix'],
          vUserName: this.allUsersInHash[r.request_by_id] ? this.allUsersInHash[r.request_by_id]['name'] : r.request_by_id,
          vSubjectTitle: this.getSubjectTitle(r.subject_id),
          vStart: start.format('ll LT'),
          vEnd: end.format('ll [' + this.i18n['at_time_lower'] + '] LT'),
          vResponderName: this.allUsersInHash[r.respond_by_id] ? this.allUsersInHash[r.respond_by_id]['name'] : r.respond_by_id
        }
      })
    },
    sortedRequests () {
      const dir = this.sortDir === 'asc' ? 1 : -1
      const key = this.sortBy
      const locale = this.locale || 'da'

      return this.formattedRequests.slice().sort((a, b) => {
        const va = this.getSortValue(a, key)
        const vb = this.getSortValue(b, key)

        if (DATE_SORT_KEYS[key]) {
          return (va - vb) * dir
        }

        if (typeof va === 'string' || typeof vb === 'string') {
          return String(va || '').localeCompare(String(vb || ''), locale, { sensitivity: 'base' }) * dir
        }

        if (va < vb) return -1 * dir
        if (va > vb) return 1 * dir
        return 0
      })
    }
  },
  watch: {
    schoolId: {
      immediate: true,
      handler () {
        this.readRequests()
        this.readSubjects()
      }
    }
  },
  methods: {
    toggleSchoolYear (yearName) {
      const idx = this.selectedSchoolYears.indexOf(yearName)
      if (idx < 0) {
        this.selectedSchoolYears.push(yearName)
      } else {
        this.selectedSchoolYears.splice(idx, 1)
      }
    },
    requestInSchoolYear (request, year) {
      const start = year.employee_time_start || year.student_time_start
      const end = year.employee_time_end || year.student_time_end
      if (!start || !end || !request.start_time) {
        return true
      }
      const requestStart = moment(request.start_time)
      return requestStart.isSameOrAfter(moment(start).startOf('day')) &&
        requestStart.isSameOrBefore(moment(end).endOf('day'))
    },
    setTheEditRequest (request) {
      if (!this.isUserView || request.state !== 'requested') {
        return
      }
      this.theEditRequest = request
    },
    getSubjectTitle (subjectId) {
      const subject = this.subjects.find(subject => subject.id * 1 === subjectId * 1)
      return subject ? subject.name : subjectId
    },
    getSortValue (request, key) {
      if (DATE_SORT_KEYS[key]) {
        const value = request[key]
        return value ? moment(value).valueOf() : 0
      }
      const value = request[key]
      return value == null ? '' : value
    },
    setSort (key) {
      if (SORTABLE_KEYS.indexOf(key) === -1) {
        return
      }
      if (this.sortBy === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortBy = key
        this.sortDir = key === 'created_at' ? 'desc' : 'asc'
      }
      this.persistSort()
    },
    persistSort () {
      try {
        window.localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify({
          sortBy: this.sortBy,
          sortDir: this.sortDir
        }))
      } catch (e) {
        // ignore storage write errors (e.g. private mode / quota)
      }
    },
    restoreSort () {
      try {
        const raw = window.localStorage.getItem(SORT_STORAGE_KEY)
        if (!raw) return
        const saved = JSON.parse(raw)
        if (!saved) return
        if (SORTABLE_KEYS.indexOf(saved.sortBy) > -1) {
          this.sortBy = saved.sortBy
        }
        if (saved.sortDir === 'asc' || saved.sortDir === 'desc') {
          this.sortDir = saved.sortDir
        }
      } catch (e) {
        // ignore malformed storage
      }
    },
    readRequests () {
      if (!this.schoolId) {
        return
      }
      this.isLoading = true
      docendo.listRequests(this.schoolId, success => {
        this.requests = success
        this.isLoading = false
      }, failure => {
        console.log({ failure })
      })
    },
    readSubjects () {
      this.isLoadingSubjects = true
      docendo.schoolAbsenceSubjectsList(this.schoolId, true, success => {
        this.isLoadingSubjects = false
        this.subjects = success
      }, failure => {
        console.log('something went wrong')
      })
    },
    onSubmittedAnswer () {
      this.readRequests()
    },
    onCloseAnswerRequest () {
      this.theAnswerRequest = null
    }
  }
}
</script>

<style scoped>
.user-requests-list:not(.user-requests-list--user) {
  padding: 1rem !important;
}

.user-requests-list__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.do-table tbody tr:nth-child(2n) {
  background-color: #f9fafb;
}

.do-table tbody tr td {
  border-left: 1px solid #e2e8f0;
}

.do-table thead th:last-child,
.do-table tbody tr td:last-child {
  text-align: center;
  width: 100px;
}

.d-bagde {
  display: inline-block;
  border-radius: 4px;
  background-color: #fef3f2;
  border: 1px solid #fbdfde;
  color: #b91c1c;
  padding: 2px 4px;
  font-size: 11px;
  width: 80px;
  text-align: center;
}

.d-bagde.d-badge--yellow {
  border: 1px solid #facc17;
  background-color: #fefce8;
  color: #a26206;
}

.d-bagde.d-badge--green {
  border: 1px solid #ceeed7;
  background-color: #f0fef4;
  color: #008236;
}

.td-date {
  width: 140px;
  text-align: center;
}

.text-align-left {
  text-align: left !important;
}

.do-pill--white {
  background: #fff;
  font-weight: bold;
}

.do-table tbody tr:hover {
  background: aliceblue;
}

.user-requests-list.user-requests-list--user .do-table tbody tr:hover {
  cursor: pointer;
}

.td-created {
  width: 140px;
}

.th-sorter {
  cursor: pointer;
  user-select: none;
}

.th-sorter:hover {
  transition: 0.15s;
}

.th-sorter:active {
  transform: scale(0.98);
}

.th-sorter.th-sorter--active {
  color: #6772e5;
}

.pad-1-important {
  padding: 1rem !important;
}
</style>



// WEBPACK FOOTER //
// UserRequestList.vue?7bf8b40e